import { classNames } from "@/utils/classNames";
import { Icon } from "./Icon";
import {
  ChangeEvent,
  ChangeEventHandler,
  Fragment,
  ReactElement,
  ReactNode,
  useContext,
} from "react";
import { Box, VBox } from "./Box";
import { observer } from "mobx-react";
import { Alert } from "./Alert";

import { useDispatch, useTranslation } from "@/stores/RootStoreContext";

import { DCEvent } from "@/events/eventsDomain";
import { BooleanFieldModel, OptionFieldModel } from "@/common/commonForms";
import { TableContext } from "./TableContext";
import { isTMessage, TMessage } from "@/i18n/tt";

export type CheckboxProps = {
  name?: string;
  label?: ReactElement | string | number | TMessage;
  descriptionText?: ReactElement | string | number | TMessage;
  checked?: boolean;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  value?: string;
  id?: string;
  className?: string;
  tooltip?: string | TMessage;
};

export const Checkbox = observer(
  ({
    name,
    label,
    descriptionText,
    checked,
    disabled,
    onChange,
    value,
    id,
    className,
    tooltip,
  }: CheckboxProps) => {
    const tableContext = useContext(TableContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const checkbox = (
      <div className={classNames(className, "flex relative w-5 leading-6 flex-none")}>
        <input
          type="checkbox"
          className={classNames(
            "top-0.5",
            "absolute",
            "appearance-none",
            "w-5",
            "h-5",
            "border-2",
            "rounded-sm",
            disabled ? "cursor-default" : "cursor-pointer",
            disabled && checked && ["border-gray", "bg-gray"],
            disabled && !checked && ["border-gray", "bg-default"],
            !disabled &&
              checked && [
                "border-primary",
                "bg-primary",
                "hover:border-primary-dark",
                "hover:bg-primary-dark",
              ],
            !disabled &&
              !checked && [
                "border-gray-hard",
                "bg-default",
                "hover:border-primary",
                "hover:bg-primary-light",
              ],
          )}
          name={name}
          checked={checked}
          onChange={(event) => {
            const result = onChange?.(event);
            if (Array.isArray(result)) {
              dispatch(result);
            }
          }}
          disabled={disabled}
          value={value}
          id={id}
        />
        <div
          className={classNames(
            "top-0.5",
            "absolute",
            "w-5",
            "h-5",
            "text-lg",
            "justify-center",
            "items-center",
            "color-primary-contrast",
            "pointer-events-none",
            checked ? "flex" : ["hidden"],
          )}
        >
          <Icon name={"check"} weight={"bold"} />
        </div>
      </div>
    );

    if (label) {
      return (
        <label
          className={classNames(
            "flex",
            "gap-2",
            "items-start",
            disabled ? ["cursor-default", "subtle"] : ["cursor-pointer", "color-default"],
          )}
        >
          {descriptionText ? (
            <Box tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}>
              <VBox paddingTop={"sm"}>{checkbox}</VBox>
              <VBox gap={false}>
                <div className={"leading-6"}>{isTMessage(label) ? t(label) : label}</div>
                <div className={"subtle text-sm"}>
                  {isTMessage(descriptionText) ? t(descriptionText) : descriptionText}
                </div>
              </VBox>
            </Box>
          ) : (
            <Box tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}>
              {checkbox}
              <div className={"leading-6"}>{isTMessage(label) ? t(label) : label}</div>
            </Box>
          )}
        </label>
      );
    }

    if (tableContext) {
      return (
        <div
          className="flex"
          data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}
        >
          {checkbox}
          &nbsp;
        </div>
      );
    }
    return (
      <div data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}>
        {checkbox}
      </div>
    );
  },
);

export type MCheckboxProps = CheckboxProps & { model: BooleanFieldModel };

export const MCheckbox = observer(({ model, ...props }: MCheckboxProps) => {
  return (
    <Checkbox
      {...props}
      checked={model.value}
      onChange={props.onChange ?? ((event) => model.setValue(event.target.checked))}
    />
  );
});

export type CheckboxGroupProps<T> = {
  name?: string;
  options: T[];
  idFn: (item: T) => string;
  labelFn: (item: T) => string | ReactElement;
  checkedFn: (item: T) => boolean;
  disabledFn?: (item: T) => boolean;
  tooltipFn?: (value: T) => string | TMessage | undefined;
  extraContentFn?: (item: T, checked: boolean) => ReactNode;
  disabled?: boolean;
  horizontal?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    option: T,
    checked: boolean,
  ) => void | DCEvent[];
};

export function CheckboxGroup<T>({
  name,
  horizontal,
  options,
  idFn,
  labelFn,
  checkedFn,
  disabledFn,
  extraContentFn,
  disabled,
  onChange,
  tooltipFn,
}: CheckboxGroupProps<T>) {
  const dispatch = useDispatch();
  return (
    <Box vertical={!horizontal} gap={horizontal ? "md" : "sm"}>
      {options.map((option) => (
        <Fragment key={idFn(option)}>
          <Checkbox
            name={name}
            disabled={disabled ?? disabledFn?.(option)}
            label={labelFn(option)}
            onChange={(event) => {
              const result = onChange?.(event, option, event.target.checked);
              if (Array.isArray(result)) {
                dispatch(result);
              }
            }}
            checked={checkedFn(option)}
            tooltip={tooltipFn?.(option)}
          />
          {extraContentFn?.(option, checkedFn(option))}
        </Fragment>
      ))}
    </Box>
  );
}

export const MCheckboxGroup = observer(function MCheckboxGroup<T>({
  model,
  name,
  options,
  idFn,
  labelFn,
  checkedFn,
  disabledFn,
  tooltipFn,
  disabled,
  horizontal,
  onChange,
  extraContentFn,
}: {
  model: OptionFieldModel<T>;
} & Partial<CheckboxGroupProps<T>>) {
  const { t } = useTranslation();
  return (
    <Box vertical={!horizontal} gap={horizontal ? "md" : "sm"}>
      {model.error !== undefined && <Alert type={"error"} message={<>{t(model.error)}</>} />}
      {(options ?? model.options).map((option) => (
        <Fragment key={(idFn ?? model.getId)(option)}>
          <Checkbox
            name={name}
            disabled={disabled || model.isDisabled(option) || disabledFn?.(option)}
            label={labelFn?.(option) ?? model.getLabel(option)}
            onChange={(event) => {
              if (onChange) {
                onChange(event, option, event.target.checked);
              } else {
                model.toggle(option);
              }
            }}
            checked={checkedFn?.(option) ?? model.isSelected(option)}
            tooltip={tooltipFn?.(option) ?? model.getTooltip(option)}
          />
          {extraContentFn?.(option, checkedFn?.(option) ?? model.isSelected(option))}
        </Fragment>
      ))}
    </Box>
  );
});
